/* eslint-disable no-plusplus */
import {
  Box,
  Grid,
  IconButton,
  InputBase,
  makeStyles,
  Paper,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { Search as SearchIcon } from '@material-ui/icons';
import _, { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { resetQuery } from '../../features/controller/controllerSlice';
import QCXButton from '../button/QCXButton';
import QCXDataGrid from '../data-grid/QCXDataGrid';
import QCXDataGridNew from '../data-grid/QCXDataGridNew';

const useStyles = makeStyles((theme) => ({
  onlySearchBar: {
    padding: '4px 8px 4px 0px',
  },
  search: {
    paddingLeft: '0.3rem',
    display: 'flex',
    alignItems: 'center',
    borderRadius: '0',
    background: 'transparent',
    border: '0',
    borderBottom: '1px solid rgb(0 0 0 / 54%)',
    '&:hover': {
      borderBottomColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
  },
  input: {
    marginLeft: theme.spacing(1),
    flex: 1,
    '&::placeholder': {
      textTransform: 'uppercase',
    },
  },
  iconButton: {
    padding: 10,
  },
  firstButton: {
    width: '100%',
    minHeight: '44px',
    '& .MuiButton-startIcon': {
      marginRight: '4px',
    },
  },
  otherButton: {
    width: '100%',
    minHeight: '44px',
  },
  table: {
    paddingTop: theme.spacing(3),
  },
}));

export default function QCXDataGridControlNew({
  columns = [],
  rows = [],
  pageSize: initialPageSize = 5,
  rowsPerPageOptions = [5, 10, 25, 50, 100],
  disableColumnMenu = true,
  disableColumnReorder = true,
  disableColumnResize = true,
  loading = false,
  controlButtons = [],
  filterPropGetter,
  entityPluralName,
  defaultQuerySearch = '',
  querySearch = undefined,
  showInactiveRows = false,
  buttonGridSize = undefined,
  searchGridSize = undefined,
  ignoreControlButtonsLimit = false,
  ...restProps
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();

  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const [filteredData, setFilteredData] = useState([]);
  const [filterableColumns, setFilterableColumns] = useState([]);
  const [filterInputValue, setFilterInputValues] = useState(defaultQuerySearch || '');
  const [isFiltering, setIsFiltering] = useState(false);
  const [activeRows, setActiveRows] = useState([]);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);

  useEffect(() => {
    const handleQuerySearch = () => {
      if (querySearch) {
        setFilterInputValues(querySearch);
        dispatch(resetQuery());
      }
    };

    handleQuerySearch();
  }, [querySearch, dispatch]);

  const buttonsQuantity = useMemo(() => controlButtons?.length, [controlButtons]);

  useEffect(() => {
    if (!showInactiveRows) {
      setActiveRows(rows.filter((row) => row.active !== false));
      return;
    }

    setActiveRows(rows);
  }, [rows, showInactiveRows]);

  useEffect(() => {
    setFilteredData(activeRows);
  }, [activeRows]);

  useEffect(() => {
    setFilterableColumns(columns.filter((item) => _.isFunction(item.valueGetter)));
  }, [columns]);

  const filter = useCallback(() => {
    const allData = activeRows;
    const filterParts = filterInputValue?.split('|');

    if (!filterParts?.length || !filterInputValue?.trim()) {
      setFilteredData(allData);
      return;
    }

    const resultData = [];

    for (let i = 0; i < allData.length; i++) {
      const row = allData[i];

      let filterableRowConcat = '';
      for (let j = 0; j < filterableColumns.length; j++) {
        filterableRowConcat += filterableColumns[j]?.valueGetter({ row }) ?? '';
      }

      const containsFilter = filterParts.every((item) =>
        filterableRowConcat.toLowerCase().includes(item.toLowerCase().trim())
      );

      if (containsFilter) {
        resultData.push(row);
      }
    }

    setFilteredData(resultData);
  }, [activeRows, filterInputValue, filterableColumns]);

  useEffect(() => {
    setIsFiltering(true);
    filter();
    setIsFiltering(false);
  }, [filter]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredData.length / pageSize) - 1);

    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredData, page, pageSize]);

  const handleChangeInputFilterField = useCallback(({ target: { value } }) => {
    setFilterInputValues(value);
    setPage(0);
  }, []);

  return (
    <Grid container item justify="space-between" alignItems="center" spacing={0} xs={12} sm={12} md={12} xl={12}>
      <Box width="100%" display="flex" flexDirection="row" flexWrap="wrap" style={{ gap: '0.75rem' }}>
        <Box
          flex={1}
          minWidth="600px"
          style={{
            paddingLeft: '5px',
            ...(isSmDown || buttonsQuantity === 0
              ? {
                  paddingRight: '12px',
                }
              : {}),
          }}
        >
          <Paper className={classes.search} elevation={0}>
            <InputBase
              className={classes.input}
              placeholder={t('com.muralis.qcx.filtro.placeholder', {
                colunas: filterableColumns.map((item) => item.headerName.toUpperCase()).join(' | '),
              }).toUpperCase()}
              onChange={handleChangeInputFilterField}
              value={filterInputValue}
              inputProps={{ 'aria-label': t('com.muralis.qcx.pesquisar').toUpperCase() }}
            />
            <IconButton className={classes.iconButton} aria-label="search" disabled>
              <SearchIcon />
            </IconButton>
          </Paper>
        </Box>

        <Box
          style={{ gap: '0.5rem' }}
          display="flex"
          justify="flex-end"
          alignItems="center"
          flexWrap="wrap"
          spacing={1}
        >
          {controlButtons.map(({ to, description, startIcon, tooltip, onClick, ...restControlButtonProps }, index) => {
            if (index > 2 && !ignoreControlButtonsLimit) {
              return null;
            }

            const isFirstButton = index === 0;

            return (
              <Box key={`control-button-${index + 1}`}>
                <QCXButton
                  className={isFirstButton ? classes.firstButton : classes.otherButton}
                  color={isFirstButton ? 'primary' : 'secondary'}
                  startIcon={startIcon}
                  tooltip={tooltip && !isEmpty(tooltip)}
                  tooltipDescription={tooltip?.description}
                  {...(to ? { to, component: Link } : {})}
                  {...(onClick ? { onClick } : {})}
                  {...restControlButtonProps}
                >
                  {description}
                </QCXButton>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Grid container item direction="row" className={classes.table}>
        <QCXDataGridNew
          columns={columns}
          rows={filteredData}
          disableColumnMenu={disableColumnMenu}
          disableColumnReorder={disableColumnReorder}
          disableColumnResize={disableColumnResize}
          loading={loading || isFiltering}
          page={page}
          onPageChange={(params) => {
            if (typeof params === 'number') {
              setPage(params);
            } else {
              setPage(params.page);
            }
          }}
          pageSize={pageSize}
          onPageSizeChange={(params) => {
            const novoPageSize = typeof params === 'number' ? params : params.pageSize;
            setPageSize(novoPageSize);
            setPage(0);
          }}
          rowsPerPageOptions={rowsPerPageOptions}
          pagination
          filterModel={restProps?.filterModel}
          {...restProps}
        />
      </Grid>
    </Grid>
  );
}